import type { LucideIcon } from "lucide-react"
import { useState, type ReactNode } from "react"
import { User } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Popover/Popover"
import { Separator } from "../Separator/Separator"

export interface AccountMenuItem {
  id: string
  label: ReactNode
  icon?: LucideIcon
  onSelect?: () => void
  active?: boolean
  group?: string
}

export interface AccountMenuProps {
  name: ReactNode
  avatarSlot?: ReactNode
  items: AccountMenuItem[]
  side?: "top" | "bottom" | "left" | "right"
  align?: "start" | "center" | "end"
  sideOffset?: number
  triggerClassName?: string
  className?: string
  iconOnly?: boolean
}

const DEFAULT_GROUP = "default"

function groupItems(items: AccountMenuItem[]): AccountMenuItem[][] {
  const order: string[] = []
  const map = new Map<string, AccountMenuItem[]>()
  for (const item of items) {
    const key = item.group ?? DEFAULT_GROUP
    if (!map.has(key)) {
      map.set(key, [])
      order.push(key)
    }
    map.get(key)!.push(item)
  }
  return order.map((key) => map.get(key)!)
}

export function AccountMenu({
  name,
  avatarSlot,
  items,
  side = "top",
  align = "start",
  sideOffset = 8,
  triggerClassName,
  className,
  iconOnly = false,
}: AccountMenuProps) {
  const groups = groupItems(items)
  const ariaLabel = typeof name === "string" ? name : undefined
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={iconOnly ? ariaLabel : undefined}
          className={cn(
            "flex items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer min-w-0",
            iconOnly
              ? "h-7 w-7 justify-center p-0"
              : "gap-1.5 -ml-1 px-1 py-0.5",
            triggerClassName,
          )}
        >
          {avatarSlot ?? <User size={15} strokeWidth={1.5} className="shrink-0" />}
          {!iconOnly && (
            <span className="text-label text-foreground truncate">{name}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn("w-52 p-1 overflow-hidden", className)}
      >
        {groups.map((group, groupIdx) => (
          <div key={groupIdx}>
            {groupIdx > 0 && <Separator className="my-1" />}
            <div className="flex flex-col gap-0.5">
              {group.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      item.onSelect?.()
                      setOpen(false)
                    }}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-md text-label w-full text-left transition-colors cursor-pointer",
                      item.active
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                    )}
                  >
                    {Icon && <Icon size={14} strokeWidth={1.5} />}
                    {item.label}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  )
}
